import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { isSuperAdminByRoleId } from '@packages/types/auth';
import { type Permission } from '@packages/types/auth';
import { usePolicyStore } from '@/policy/policyStore';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: keyof Permission;
  requiredRole?: 'SuperAdmin' | 'Admin';
  fallbackPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredRole,
  fallbackPath = '/login',
}) => {
  const { isAuthenticated, user } = useAuthStore();
  const { permissions, isSuperAdmin } = usePolicyStore();
  const location = useLocation();
  const hasSuperAdminRole = isSuperAdmin || isSuperAdminByRoleId(user?.role_id);

  // sessionStorage에 실제 토큰이 있는지 확인 (동기적으로)
  // Zustand isAuthenticated가 true여도 토큰이 없으면 AuthProvider가 logout() 호출하기 전에
  // API 요청이 나가서 401이 발생하는 것을 방지
  let hasToken = false;
  try {
    hasToken = !!sessionStorage.getItem('access_token');
  } catch {
    hasToken = false;
  }

  if (!hasToken) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // 토큰은 있는데 persist rehydration 전 isAuthenticated=false 인 짧은 구간 허용 (모바일 로그인 직후)
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // 필요한 역할이 있는 경우 역할 체크
  if (requiredRole) {
    if (requiredRole === 'SuperAdmin' && !hasSuperAdminRole) {
      // SuperAdmin이 필요한데 SuperAdmin이 아닌 경우 메인 페이지로 리다이렉트
      return <Navigate to="/" replace />;
    }
  }

  // 권한이 필요한 경우 권한 체크
  if (requiredPermission) {
    if (permissions === null) {
      return null;
    }
    if (!permissions[requiredPermission]) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};
